/**
 * ## FiltersComponent.tsx ##
 * This file contains FiltersComponent component
 * @packageDocumentation
 */

import * as React from 'react';

import Popover, { PopoverProps } from 'antd/es/popover';

import '@common/react/scss/components/filtersComponent.scss';

/**
 * FiltersComponent component props
 *
 * @interface FiltersComponentProps
 */
export interface FiltersComponentProps {
	/**
	 * popover open
	 */
	open?: boolean;
	/**
	 * open onChange handler
	 */
	onOpenChange?: React.Dispatch<React.SetStateAction<boolean>>;
	/**
	 * filters array to solve not empty filters count
	 */
	filters: Array<() => boolean>;
	/**
	 * popover children
	 */
	tooltipValues: JSX.Element;
	/**
	 * filters clean callback
	 */
	filtersClean?: () => void;
	/**
	 * apply button visibility
	 */
	apply?: boolean;
	/**
	 * disable filters component
	 */
	disabled?: boolean;
	/**
	 * antd popover style
	 */
	popoverStyle?: React.CSSProperties;
	/**
	 * default popover open/close button classname
	 */
	buttonClassName?: string;
	/**
	 * popover className
	 */
	popoverClassName?: string;
	/**
	 * get custom popup container
	 */
	getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
	/**
	 * custom popup trigger node
	 */
	triggerNode?: React.ReactNode;
	/**
	 * popover title
	 */
	title?: string;
	/**
	 * popover placement
	 */
	placement?: PopoverProps['placement'] | null;
	/**
	 * apply onClick handler
	 */
	onApply?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * FiltersComponent component.
 *
 * @param props - FiltersComponentProps
 * @type {React.FC<FiltersComponentProps>}
 * @returns React.ReactElement
 */
const FiltersComponent: React.FC<FiltersComponentProps> = ({
	filters, tooltipValues, apply, filtersClean, disabled = false, open, onOpenChange, onApply, ...rest
}) => {
	const {
		buttonClassName, popoverStyle, popoverClassName, getPopupContainer, triggerNode, title = 'Filters', placement = 'bottomRight',
	} = rest;
	const [visible, setVisible] = React.useState<boolean>(false);
	const count = filters.reduce((res, filter) => (filter() ? res + 1 : res), 0);

	const onVisibleChange = React.useCallback((visible) => {
		setVisible(visible);
		onOpenChange && onOpenChange(visible);
	}, []);

	const clean = React.useCallback(() => {
		if (filtersClean) {
			filtersClean();
		}
	}, [filtersClean]);

	const close = React.useCallback(() => {
		setVisible(false);
		onOpenChange && onOpenChange(false);
	}, []);

	return (
		<div className="filters-component-container pull-right">
			{disabled
				? <>
					<button type="button" disabled={disabled} className="btn btn-default filters-component-button">
						<i className="fa fa-filter" aria-hidden="true" />
					</button>
				</>
				: (
					<Popover
						trigger="click"
						title={title}
						overlayStyle={popoverStyle}
						className={popoverClassName}
						open={open ?? visible}
						getPopupContainer={getPopupContainer}
						onOpenChange={onVisibleChange}
						content={
							<>
								{tooltipValues}
								{(apply || filtersClean)
									&& <div className="filters-apply-btn-container">
										{filtersClean
											? (
												<button
													type="button"
													className={`btn btn-danger ${apply ? 'mr10' : ''}`}
													disabled={count === 0}
													onClick={clean}
												>
													Clear
												</button>
											) : null
										}
										{apply
											? (
												<button
													type="button"
													className="btn btn-primary"
													onClick={(e) => {
														onApply && onApply(e);
														close();
													}}
												>
													Apply
												</button>
											)
											: null
										}
									</div>
								}
							</>
						}
						overlayClassName={`filters-component-tooltip ${popoverClassName}`}
						placement={placement === null ? undefined : placement}
					>
						{triggerNode || <button
							type="button"
							className={`btn btn-default filters-component-button ${buttonClassName || ''}`}
						>
							<i className="fa fa-filter" aria-hidden="true" />
						</button>}
					</Popover>
				)}
			{count > 0 && !disabled
				&& <div
					className="badge dashboard-container-user-block__badge"
					onClick={clean}
				>
					<span>{count}</span>
					<i className="fa fa-close" />
				</div>
			}
		</div>
	);
};

export default FiltersComponent;
